import React, { useMemo } from "react";
import { Activity, Bot, CalendarClock, CheckCircle2, CircleDot, UserPlus } from "lucide-react";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";

const relativeTime = (value) => {
  if (!value) return "recently";
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.max(0, Math.floor(diff / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const statusLabel = {
  backlog: "Backlog",
  in_progress: "In Progress",
  done: "Done",
};

const ActivityFeed = ({ tasks = [], recommendations = [], selectedProject, presence = [] }) => {
  const events = useMemo(() => {
    const taskEvents = tasks
      .slice()
      .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
      .slice(0, 5)
      .map((task) => ({
        id: `task-${task.id}`,
        icon: task.status === "done" ? CheckCircle2 : CircleDot,
        title: task.status === "done" ? "Task completed" : "Task updated",
        description: task.title,
        meta: statusLabel[task.status] || "Task",
        timestamp: task.updated_at || task.created_at,
      }));

    const aiEvents = recommendations.slice(0, 2).map((item, index) => ({
      id: `ai-${index}`,
      icon: Bot,
      title: item.title,
      description: item.description,
      meta: "AI insight",
      timestamp: new Date().toISOString(),
    }));

    const presenceEvents = presence.slice(0, 2).map((user) => ({
      id: `presence-${user.user_id}`,
      icon: UserPlus,
      title: "Collaborator active",
      description: `${user.user_name} is in ${selectedProject?.name || "this project"}`,
      meta: "Live",
      timestamp: user.last_seen,
    }));

    return [...presenceEvents, ...aiEvents, ...taskEvents].slice(0, 8);
  }, [presence, recommendations, selectedProject?.name, tasks]);

  return (
    <Card className="dashboard-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="h-4 w-4 text-[#5F2AA8]" />
          Activity Timeline
        </CardTitle>
        <CardDescription>Realtime work signals and AI-generated recommendations.</CardDescription>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="rounded-2xl border border-dashed bg-white/60 p-5 text-sm text-muted-foreground">
            Activity will appear as tasks, comments, and AI actions happen.
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event, index) => (
              <div key={event.id} className="relative flex gap-3">
                {index < events.length - 1 && (
                  <div className="absolute left-4 top-8 h-[calc(100%+0.25rem)] w-px bg-border" />
                )}
                <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-indigo-100 bg-[#f3eeff] shadow-sm">
                  <event.icon className="h-4 w-4 text-[#4B1E87]" />
                </div>
                <div className="min-w-0 flex-1 pb-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{event.title}</div>
                      <p className="mt-0.5 line-clamp-2 text-xs leading-5 text-muted-foreground">
                        {event.description}
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {event.meta}
                    </Badge>
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                    <CalendarClock className="h-3 w-3" />
                    {relativeTime(event.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
